/** 11 §4 disc trails: FX_TRAIL_SEC of travel sampled from interpolated
 *  positions each frame. Solid + accent core in FLIGHT, dashed no-core in
 *  RETURNING, none on GROUND/HELD; cleared on state change; the accent
 *  switches instantly on a perfect deflect. */
import { FX_TRAIL_SEC } from './constants.js'
import type { DiscStateName, Team } from '../../../sim/types.js'

export interface TrailSample { x: number; z: number; t: number }
export interface Trail { kind: 'solid' | 'dashed'; team: Team; samples: TrailSample[]; state: DiscStateName }

export class TrailSet {
  readonly trails = new Map<number, Trail>()

  /** Feed one disc's current interpolated position. Returns the trail or null. */
  push(id: number, state: DiscStateName, team: Team, x: number, z: number, renderTime: number): Trail | null {
    if (state !== 'FLIGHT' && state !== 'RETURNING') { this.trails.delete(id); return null }
    let tr = this.trails.get(id)
    const kind = state === 'FLIGHT' ? 'solid' : 'dashed'
    if (!tr || tr.state !== state) { tr = { kind, team, samples: [], state }; this.trails.set(id, tr) }
    tr.team = team
    tr.kind = kind
    const last = tr.samples[tr.samples.length - 1]
    if (!last || last.t < renderTime) tr.samples.push({ x, z, t: renderTime })
    const cutoff = renderTime - FX_TRAIL_SEC * 1000
    while (tr.samples.length > 1 && tr.samples[0].t < cutoff) tr.samples.shift()
    if (tr.samples.length > 24) tr.samples.shift()
    return tr
  }

  /** 11 §4 style per disc state: FLIGHT solid + accent core, RETURNING dashed no core, otherwise no trail. */
  styleFor(state: DiscStateName): { dashed: boolean; core: boolean } {
    if (state === 'FLIGHT') return { dashed: false, core: true }
    if (state === 'RETURNING') return { dashed: true, core: false }
    return { dashed: false, core: false }
  }

  /** `deflectPerfect`: the trail's accent core switches to the new owner immediately. */
  setTeam(id: number, team: Team): void { const tr = this.trails.get(id); if (tr) tr.team = team }
  /** `clash`: both trails cut. */
  cut(id: number): void { this.trails.delete(id) }
  clear(): void { this.trails.clear() }
  get(id: number): Trail | undefined { return this.trails.get(id) }
}
